import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Container,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import PageTitle from "../../components/common/pages/PageTitle";
import { observer } from "mobx-react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SortType } from "../../../models/SortType";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import CustomTextField from "../../components/common/textFields/TextField";
import { FilterMenuType, FilterSidebarMenu } from "../../../models/FilterMenu";
import { ModuleType } from "../../../models/ModuleType";
import FilterSidebar from "../../components/common/filter/FilterSidebar";
import { useDebounce } from "../../components/common/hooks/Debounce";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AppButton from "../../components/common/buttons/AppButton";
import InventorySwagpackTable from "../../components/Inventory/InventorySwagpackTable";
import { SwagpackFilters } from "../../../models/inventory/InventorySwagpackFilter";
import CustomTypography from "../../components/common/form/CustomTypography";

const InventorySwagpackDetails: React.FC = observer(() => {
  const theme = useTheme();
  const { inventoryStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadingCountReport, setDownloadingCountReport] = useState(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const filterFields = ["out_of_stock"];

  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const initialFilters: SwagpackFilters = {
    page_no: parseInt(searchParams.get("page") || "1"),
    search_text: searchParams.get("search_text") || undefined,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    out_of_stock:
      searchParams.get("out_of_stock") === "true"
        ? true
        : searchParams.get("out_of_stock") === "false"
          ? false
          : undefined,
    limit: parseInt(searchParams.get("limit") || "30"),
  };

  const [filters, setFilters] = useState<SwagpackFilters>(initialFilters);
  const [params, setParams] = useState<SwagpackFilters>(initialFilters);
  const { id } = useParams();
  const inventoryId = Number(id);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const newSearch = debouncedSearchTerm || undefined;
    setParams((prev) => {
      if (prev.search_text === newSearch) return prev;
      return { ...prev, search_text: newSearch, page_no: 1 };
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await inventoryStore.getSwagpacks(inventoryId, params);
      } catch {
        showError("Error", "Failed to fetch swagpack data");
      } finally {
      }
    };
    fetchData();
  }, [inventoryId, params, inventoryStore, showError]);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setParams((prev) => ({
        ...prev,
        search_text: searchTerm || undefined,
        page_no: 1,
      }));
    }
  };

  const handleFilterToggle = () => setOpenFilter(!openFilter);

  const handleFilterChange = useCallback(
    (key: keyof SwagpackFilters, value: any) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(
    () => [
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Availability",
        list: [
          { id: "false", value: "In Stock" },
          { id: "true", value: "Out of Stock" },
        ],
        value:
          filters.out_of_stock !== undefined
            ? [
                {
                  id: filters.out_of_stock.toString(),
                  value: filters.out_of_stock ? "Out of Stock" : "In Stock",
                },
              ]
            : [],
        onChange: (selected: FilterValue[]) => {
          handleFilterChange(
            "out_of_stock",
            selected.length ? selected[0].id === "true" : undefined,
          );
        },
      },
    ],
    [filters, handleFilterChange],
  );

  const handleApplyFilters = () => {
    const updatedParams = {
      ...params,
      ...filters,
      search_text: searchTerm || undefined,
      page_no: 1,
    };
    setParams(updatedParams);

    const query = new URLSearchParams();
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (value !== undefined) query.set(key, value.toString());
    });
    navigate(`?${query.toString()}`, { replace: true });
    handleFilterToggle();
  };

  const handleClearFilters = () => {
    const cleared = {
      page_no: 1,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      limit: params.limit,
    };
    setFilters(cleared);
    setParams(cleared);
    setSearchTerm("");
    navigate(
      `?page=1&limit=${params.limit}&sort_by=created_date&sort_type=DESC`,
      { replace: true },
    );
    handleFilterToggle();
  };

  useEffect(() => {
    setFilterCount(
      filterFields.reduce(
        (count, key) =>
          filters[key as keyof SwagpackFilters] !== undefined
            ? count + 1
            : count,
        0,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handlePageChange = (_: any, page: number) => {
    setParams((prev) => ({ ...prev, page_no: page }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleBulkActions = () => {
    if (actionsAnchorRef.current) {
      setAnchorEl(actionsAnchorRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadCountReportCsv = async () => {
    handleClose();

    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }

    setDownloadingCountReport(true);
    try {
      const response = await inventoryStore.downloadCountReportCsv(inventoryId);

      if (!response?.data) {
        showError("Error", "No data received for count report");
        return;
      }

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        response.filename || `inventory-${inventoryId}-count-report.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      showSuccess("Count report downloaded successfully");
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to download count report CSV";
      showError("Error", message);
    } finally {
      setDownloadingCountReport(false);
    }
  };

  return (
    <Container
      sx={{
        paddingTop: 2,
      }}
      maxWidth="xl"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          color: theme.palette.customColors.greyTextColor,
          "&:hover": {
            color: theme.palette.customColors.greyTextColor,
          },
          "&:active": {
            color: theme.palette.customColors.greyTextColor,
          },
          mb: 2,
          maxWidth: "60px",
        }}
        onClick={() => {
          navigate(-1);
        }}
      >
        <ArrowBackIcon sx={{ fontSize: "16px" }} />
        <span style={{ fontSize: "14px", fontWeight: "600" }}>Back</span>
      </Box>
      {!userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10rem"
          textAlign="center"
        >
          <Typography variant="h6" fontWeight="500" color="text.secondary">
            You don't have permission to view this section.
          </Typography>
        </Box>
      ) : (
        <>
          <Paper
            sx={{
              padding: 5,
              paddingBottom: 3,
              paddingTop: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <PageTitle title="Inventory Swagpacks" />
              {userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY) && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box ref={actionsAnchorRef}>
                    <AppButton
                      variant="grayscale"
                      label="Actions"
                      onClick={handleBulkActions}
                      disabled={downloadingCountReport}
                      customStyles={{ minWidth: 180 }}
                    />
                  </Box>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    disableAutoFocusItem
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow:
                            "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                          minWidth: 240,
                          overflow: "hidden",
                        },
                      },
                    }}
                  >
                    {userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY) && (
                      <MenuItem
                        onClick={downloadCountReportCsv}
                        disabled={downloadingCountReport}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          {downloadingCountReport
                            ? "Downloading..."
                            : "Download Count Report CSV"}
                        </CustomTypography>
                      </MenuItem>
                    )}
                  </Menu>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingBottom: 3,
                justifyContent: "space-between",
                gap: 4,
              }}
            >
              <CustomTextField
                value={searchTerm}
                onSearch={(query) => handleSearch(query)}
                onKeyDown={handleKeyDown}
                width={"100%"}
                placeholder="Search Label ..."
              />

              <Box sx={{ position: "relative", display: "inline-block" }}>
                <AppButton
                  variant="grayscale"
                  label="Filter"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => {
                    handleFilterToggle();
                  }}
                  customStyles={{ width: "130px" }}
                />
                {filterCount > 0 && (
                  <Badge
                    color="primary"
                    badgeContent={filterCount}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      transform: "translate(50%, -50%)",
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box>
              <InventorySwagpackTable
                maxHeight={window.innerHeight - 350}
                swagpacks={inventoryStore.swagpack}
                limit={params.limit || inventoryStore.limit}
                page_no={params.page_no}
                total_count={inventoryStore.total_count}
                total_pages={inventoryStore.total_pages}
                sortBy={params.sort_by}
                sortType={params.sort_type}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
              />
            </Box>
          </Paper>
          {openFilter && (
            <FilterSidebar
              isOpen={openFilter}
              items={filterMenu}
              onApply={handleApplyFilters}
              handleClear={handleClearFilters}
              toggleFilter={handleFilterToggle}
              disablePastDates={false}
            />
          )}
        </>
      )}
    </Container>
  );
});

export default InventorySwagpackDetails;
